import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { wotdApi } from '@/api/wotd';
import { MNote } from '@/components/mobile/MobileKit';
import { AudioPlayButton } from '@/components/ui/AudioPlayButton';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { isFeatureOn } from '@/lib/appConfig';
import { GuestWordOfTheDay } from '@/pages/public/GuestWordOfTheDay';

import { GuestShell } from './GuestShell';

/**
 * Словарь гостя (решение владельца 14.09.2026, договор guest-mode.md).
 *
 * ⚠️ ПОИСК — ЗАГЛУШКА ЗА ФЛАГОМ `guest_vocab_search`. Искать слово без входа сейчас нечем: у
 * вошедших поиск сразу сохраняет слово (ИИ и озвучка), а публичной ручки только для чтения ещё нет
 * (задача полосе B). Флаг выключен — поле есть, отправка ведёт на регистрацию. Перевод гостю не
 * открываем вовсе: это вызов OpenAI на каждый запрос без лимита на гостя.
 */
export function GuestVocabularyPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const searchOn = isFeatureOn('guest_vocab_search');
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');

  const lookup = useQuery({
    queryKey: ['public', 'vocab-lookup', submitted],
    queryFn: () => wotdApi.lookupPublic(submitted),
    enabled: searchOn && submitted.length > 0,
    retry: false,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const word = query.trim();
    if (!word) return;
    if (!searchOn) {
      navigate('/register', { state: { next: '/app/vocabulary' } });
      return;
    }
    setSubmitted(word);
  };

  const found = lookup.data?.word;
  const lang = i18n.language.split('-')[0];

  return (
    <GuestShell>
      <h1 className="font-display text-[22px] font-bold tracking-display text-on-surface">{t('guest.vocabTitle')}</h1>

      <form onSubmit={submit} className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('guest.searchPlaceholder')}
            autoCapitalize="none"
            autoCorrect="off"
          />
        </div>
        <Button type="submit">{t('guest.searchAction')}</Button>
      </form>
      {!searchOn && <p className="mx-0.5 text-xs text-on-surface-variant">{t('guest.searchHint')}</p>}

      {searchOn && submitted && lookup.isFetched && !found && <MNote icon="style" title={t('guest.notFound')} />}
      {searchOn && found && (
        <div className="rounded-[22px] bg-surface-container px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-display text-[22px] font-bold text-on-surface">{found.word}</p>
              {found.ipa && <p className="text-sm text-on-surface-variant">{found.ipa}</p>}
              <p className="mt-1 text-[15px] text-on-surface">
                {lang === 'kk' ? found.translation_kk : lang === 'en' ? found.definition : found.translation_ru}
              </p>
            </div>
            {found.audio_url && <AudioPlayButton src={found.audio_url} className="h-10 w-10 shrink-0 rounded-full" />}
          </div>
        </div>
      )}

      <GuestWordOfTheDay />

      <Link to="/register" state={{ next: '/app/vocabulary' }} className="block">
        <Button variant="secondary" fullWidth>
          {t('guest.save')}
        </Button>
      </Link>
    </GuestShell>
  );
}
